import type { NavigationItemType } from '@oppy/omniv2-core';
import { useCurrentUser } from '@oppy/omniv2-core';
import { navigations as iaNavigations } from '@oppy/omniv2-ia';
import { navigations as rcsNavigations } from '@oppy/omniv2-rcs';
import {
  type Permission,
  PERMISSIONS,
  type Resource,
  RESOURCES,
  usePermissions,
} from '@oppy/omniv2-utils';

type KnownNavigationResource =
  | 'salesCompliant'
  | 'qualityMonitoring'
  | 'RCSSetting'
  | 'RCSAgent'
  | 'RCSCampaign';

const NAVIGATION_TO_RESOURCE_MAP = {
  salesCompliant: RESOURCES.SALES_COMPLIANT,
  qualityMonitoring: RESOURCES.QUALITY_MONITORING,
  RCSSetting: RESOURCES.RCS_SETTING,
  RCSAgent: RESOURCES.RCS_AGENT,
  RCSCampaign: RESOURCES.RCS_CAMPAIGN,
} as const satisfies Record<KnownNavigationResource, Resource>;

type PermissionStrategy = {
  readonly name: string;
  readonly allowedPermissions: readonly Permission[];
  readonly checkPermission: (
    permission: Permission,
    hasPermission: (resource: Resource, permission: Permission) => boolean,
    canAccess: (resource: Resource) => boolean,
  ) => boolean;
};

const createAccessOnlyStrategy = (resource: Resource): PermissionStrategy => ({
  name: `AccessOnly(${resource})`,
  allowedPermissions: [PERMISSIONS.ACCESS],
  checkPermission: (permission, _, canAccess) =>
    permission === PERMISSIONS.ACCESS ? canAccess(resource) : false,
});

const createCrudStrategy = (resource: Resource): PermissionStrategy => ({
  name: `CRUD(${resource})`,
  allowedPermissions: [
    PERMISSIONS.READ,
    PERMISSIONS.CREATE,
    PERMISSIONS.UPDATE,
    PERMISSIONS.DELETE,
    PERMISSIONS.ACCESS,
  ],
  checkPermission: (permission, hasPermission) =>
    hasPermission(resource, permission),
});

const PERMISSION_STRATEGIES = new Map<
  KnownNavigationResource,
  PermissionStrategy
>([
  ['salesCompliant', createAccessOnlyStrategy(RESOURCES.SALES_COMPLIANT)],
  ['qualityMonitoring', createAccessOnlyStrategy(RESOURCES.QUALITY_MONITORING)],
  ['RCSSetting', createCrudStrategy(RESOURCES.RCS_SETTING)],
  ['RCSAgent', createCrudStrategy(RESOURCES.RCS_AGENT)],
  ['RCSCampaign', createCrudStrategy(RESOURCES.RCS_CAMPAIGN)],
]);

const isKnownResource = (
  resource: string,
): resource is KnownNavigationResource =>
  resource in NAVIGATION_TO_RESOURCE_MAP;

const isValidPermission = (permission: string): permission is Permission =>
  Object.values(PERMISSIONS).includes(permission as Permission);

const createPermissionValidator =
  (
    hasPermission: (resource: Resource, permission: Permission) => boolean,
    canAccess: (resource: Resource) => boolean,
  ) =>
  (resourceName: string, permissionName: string): boolean => {
    if (!isKnownResource(resourceName)) {
      return true;
    }

    if (!isValidPermission(permissionName)) {
      return false;
    }

    const strategy = PERMISSION_STRATEGIES.get(resourceName);
    if (!strategy) {
      return true;
    }

    return strategy.checkPermission(permissionName, hasPermission, canAccess);
  };

const filterNavigationByPermissions = (
  items: readonly NavigationItemType[],
  validatePermission: (resource: string, permission: string) => boolean,
): NavigationItemType[] => {
  const processItem = (
    acc: NavigationItemType[],
    item: NavigationItemType,
  ): NavigationItemType[] => {
    if (item.requiredPermission) {
      const { resource, permission } = item.requiredPermission;
      if (!validatePermission(resource, permission)) {
        return acc;
      }
    }

    const processedSubItems = item.subItems
      ? filterNavigationByPermissions(item.subItems, validatePermission)
      : undefined;

    if (!item.subItems || (processedSubItems && processedSubItems.length > 0)) {
      acc.push({
        ...item,
        subItems: processedSubItems,
      });
    }

    return acc;
  };

  return items.reduce(processItem, []);
};

export function useNavigationItems(): NavigationItemType[] {
  const currentUser = useCurrentUser();
  const { hasPermission, canAccess } = usePermissions(currentUser ?? undefined);

  const baseNavigations = [
    ...iaNavigations,
    ...(import.meta.env.VITE_RCS_ACTIVATION === 'true' ? rcsNavigations : []),
  ] as const;

  const validatePermission = createPermissionValidator(
    hasPermission,
    canAccess,
  );

  return filterNavigationByPermissions(baseNavigations, validatePermission);
}

export function getPermissionStrategies() {
  return PERMISSION_STRATEGIES;
}

export function getResourceMapping() {
  return NAVIGATION_TO_RESOURCE_MAP;
}
export { createPermissionValidator, isKnownResource, isValidPermission };

export const NAVIGATION_ITEMS: NavigationItemType[] = [];
